<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import type { HeadingLevel } from "@components/common/HeadingLevel.js";
	import Media from "@components/common/Media.svelte";
	import type { ComponentProps, Snippet } from "svelte";
	import type { ClassValue } from "svelte/elements";
	import type { SourceSetQuery } from "@core/schema/SourceSetQuery.js";

	const {
		actions,
		class: customClass,
		innerClass,
		darkOverlay,
		heading,
		media,
		paragraph,
		sources,
		contentAlign = "center",
		lcp = false,
	}: {
		actions?: Snippet;
		class?: ClassValue;
		innerClass?: ClassValue;
		darkOverlay: boolean | ClassValue;
		contentAlign?: "center" | "left" | "right";
		heading?: { level: HeadingLevel; text: string; class?: ClassValue } | Snippet;
		media?: ClassValue | Snippet<[ComponentProps<typeof Media>]>;
		paragraph?: string | Snippet;
		sources: Partial<Record<SourceSetQuery, string>> & { default: string };
		lcp?: boolean;
	} = $props();

	const { default: defaultSrc, ...sourcesets } = $derived(sources);
	const mediaProps = $derived({
		sourcesets,
		source: { type: "image/webp", src: defaultSrc },
		width: 0,
		height: 0,
		alt: "",
	});
</script>

<svelte:head>
	{#if lcp}
		<link rel="preload" href={defaultSrc} fetchpriority="high" as="image" />
	{/if}
</svelte:head>

<section
	class={[
		"full-width overflow-hidden relative flex flex-col justify-center items-stretch",
		"h-[calc(100vh-3.5rem)] md:h-[calc(100vh-6rem)] xl:h-[calc(100vh-24rem)]",
		"-mb-px" /* To prevent gap that can be caused by sub-pixel rounding */,
		customClass,
	]}
>
	<div
		class={[
			"grid-container grid-rows-[auto_1fr] h-full z-30 pb-[max(env(safe-area-inset-bottom,0),2.5rem)] px-[var(--container-gap)] relative pt-10 md:py-20",
			innerClass,
		]}
	>
		<div
			class={[
				(paragraph !== undefined || typeof heading !== "function") && "max-w-73 sm:max-w-124 xl:max-w-184",
				"flex flex-col gap-2 sm:gap-4",
				contentAlign === "center" && "text-center mx-auto",
				contentAlign === "right" && "justify-self-end text-right",
			]}
		>
			{#if typeof heading === "function"}
				{@render heading()}
			{:else if heading}
				<Heading {...heading} class={["text-white heading-1", heading.class]} />
			{/if}
			{#if typeof paragraph === "function"}
				{@render paragraph()}
			{:else if paragraph}
				<p
					class={[
						"text-white/72 font-light max-w-105 xl:text-lg/normal",
						contentAlign === "center" && "mx-auto",
						contentAlign === "right" && "mr-auto",
					]}
				>
					<Formatted text={paragraph} />
				</p>
			{/if}
		</div>

		{#if actions}
			<div class="flex gap-2 flex-col sm:flex-row flex-wrap mt-auto w-full items-center justify-center">
				{@render actions()}
			</div>
		{/if}
	</div>
	<div class="absolute inset-0" aria-hidden="true">
		{#if darkOverlay}
			<div class={["absolute inset-0 bg-black/20 z-10", darkOverlay]}></div>
		{/if}
		{#if typeof media === "function"}
			{@render media(mediaProps)}
		{:else}
			<Media
				{...mediaProps}
				wrapperClass="overflow-hidden absolute inset-0"
				class={["absolute block inset-0 size-full object-cover", media]}
			/>
		{/if}
	</div>
</section>
